import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { imgUrl } from "@/components/ImageUploader";
import { QRCodeSVG } from "qrcode.react";

const UPI_ID = "8008566725-2@ybl";
const EXPRESS_FEE = 99;

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [addr, setAddr] = useState({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", gst: "" });
  const [email, setEmail] = useState(user?.email || "");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [delivery, setDelivery] = useState("standard");
  const [payMethod, setPayMethod] = useState("razorpay");
  const [utr, setUtr] = useState("");

  const pieces = items.reduce((s, i) => s + i.quantity, 0);
  const discount = applied?.discount || 0;
  const shipping = delivery === "express" ? EXPRESS_FEE * pieces : 0;
  const total = subtotal - discount + shipping;

  const apply = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setApplied(data); toast.success(`Applied ${data.code}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid coupon"); }
  };

  const place = async () => {
    if (!email || !addr.full_name || !addr.phone || !addr.line1 || !addr.city || !addr.state || !addr.pincode) {
      return toast.error("Please fill in all address fields");
    }
    if (items.length === 0) return toast.error("Cart is empty");
    if (payMethod === "upi" && !/^[A-Za-z0-9]{10,22}$/.test(utr.trim())) return toast.error("Enter your UPI transaction ID (UTR)");
    setPlacing(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items, address: addr, coupon: applied?.code, email,
        delivery_option: delivery, payment_method: payMethod, upi_utr: payMethod === "upi" ? utr.trim() : null,
      });
      if (payMethod === "upi") { clear(); nav(`/order-success/${data.order_no}?upi=1`); return; }
      // Attempt Razorpay if configured
      if (data.razorpay_key && data.razorpay_order_id && window.Razorpay) {
        const opts = {
          key: data.razorpay_key, amount: total * 100, currency: "INR",
          name: "Nisa Libas", order_id: data.razorpay_order_id,
          prefill: { name: addr.full_name, email, contact: addr.phone },
          theme: { color: "#0B1F3A" },
          handler: async (res) => {
            await api.post("/orders/confirm-payment", { order_id: data.order_id, ...res });
            clear(); nav(`/order-success/${data.order_no}`);
          },
        };
        new window.Razorpay(opts).open();
      } else {
        // Mock path
        await api.post("/orders/confirm-payment", { order_id: data.order_id });
        clear(); nav(`/order-success/${data.order_no}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally { setPlacing(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <div className="eyebrow mb-2">Checkout</div>
      <h1 className="font-serif text-3xl md:text-4xl text-[#0B1F3A] mb-8">Complete your order</h1>
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-6">
          <section className="bg-white p-6 gold-border">
            <div className="font-serif text-lg text-[#0B1F3A] mb-4">Contact</div>
            <Input label="Email" value={email} onChange={(v) => setEmail(v)} testid="checkout-email" />
          </section>
          <section className="bg-white p-6 gold-border">
            <div className="font-serif text-lg text-[#0B1F3A] mb-4">Shipping address</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Input label="Full name" value={addr.full_name} onChange={(v) => setAddr({ ...addr, full_name: v })} testid="addr-name" />
              <Input label="Phone" value={addr.phone} onChange={(v) => setAddr({ ...addr, phone: v })} testid="addr-phone" />
              <Input label="Address line 1" value={addr.line1} onChange={(v) => setAddr({ ...addr, line1: v })} testid="addr-line1" full />
              <Input label="Address line 2 (optional)" value={addr.line2} onChange={(v) => setAddr({ ...addr, line2: v })} testid="addr-line2" full />
              <Input label="City" value={addr.city} onChange={(v) => setAddr({ ...addr, city: v })} testid="addr-city" />
              <Input label="State" value={addr.state} onChange={(v) => setAddr({ ...addr, state: v })} testid="addr-state" />
              <Input label="Pincode" value={addr.pincode} onChange={(v) => setAddr({ ...addr, pincode: v })} testid="addr-pincode" />
              <Input label="GST (optional)" value={addr.gst} onChange={(v) => setAddr({ ...addr, gst: v })} testid="addr-gst" />
            </div>
          </section>
          <DeliveryOptions value={delivery} onChange={setDelivery} pieces={pieces} />
          <PaymentOptions method={payMethod} setMethod={setPayMethod} utr={utr} setUtr={setUtr} total={total} />
        </div>
        <aside className="bg-white p-5 md:p-6 gold-border h-fit md:sticky md:top-24">
          <div className="font-serif text-lg text-[#0B1F3A] mb-4">Order summary</div>
          <div className="space-y-3 mb-4 max-h-64 overflow-auto">
            {items.map((i, idx) => (
              <div key={idx} className="flex gap-3 text-sm">
                <img src={imgUrl(i.image)} className="w-14 h-18 object-cover" alt="" />
                <div className="flex-1">
                  <div className="text-[#0B1F3A]">{i.name}</div>
                  <div className="text-xs text-[#1E293B]/60">{i.size} · {i.color} · ×{i.quantity}</div>
                </div>
                <div className="font-semibold text-[#D4AF37]">{inr(i.price * i.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-4">
            <input placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)}
              className="flex-1 border px-3 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="coupon-input" />
            <button onClick={apply} className="bg-[#0B1F3A] text-[#FFF9E6] px-4 text-xs tracking-widest" data-testid="apply-coupon">Apply</button>
          </div>
          <div className="text-sm space-y-2 border-t pt-3" style={{ borderColor: "rgba(212,175,55,0.25)" }}>
            <div className="flex justify-between"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-[#D4AF37]"><span>Discount</span><span>−{inr(discount)}</span></div>}
            <div className="flex justify-between"><span>Delivery ({delivery === "express" ? `Express · ${pieces} pc` : "Standard"})</span><span data-testid="checkout-shipping">{shipping === 0 ? "Free" : inr(shipping)}</span></div>
            <div className="flex justify-between font-serif text-lg text-[#0B1F3A] pt-2 border-t" style={{ borderColor: "rgba(212,175,55,0.25)" }}><span>Total</span><span data-testid="checkout-total">{inr(total)}</span></div>
          </div>
          <button onClick={place} disabled={placing} className="hidden md:block mt-5 w-full bg-[#D4AF37] text-[#0B1F3A] py-4 tracking-widest uppercase text-sm font-semibold hover:bg-[#c29e2e] disabled:opacity-60" data-testid="place-order-btn">
            {placing ? "Processing…" : payMethod === "upi" ? "Submit UPI payment" : "Pay & place order"}
          </button>
          <div className="hidden md:block text-[11px] text-[#1E293B]/60 mt-3 text-center">{payMethod === "upi" ? "Order is confirmed once we verify your UPI payment" : "Secured by Razorpay · UPI · Cards · Netbanking"}</div>
        </aside>
      </div>
      {/* Sticky mobile pay bar */}
      <div className="md:hidden fixed bottom-16 inset-x-0 z-30 bg-[#FCE4EC]/95 backdrop-blur-md border-t p-3" style={{ borderColor: "rgba(114,47,55,0.2)" }} data-testid="mobile-pay-bar">
        <button onClick={place} disabled={placing} className="w-full bg-[#D4AF37] text-[#0B1F3A] py-3.5 tracking-widest uppercase text-xs font-semibold disabled:opacity-60 flex items-center justify-center gap-2" data-testid="place-order-btn-mobile">
          {placing ? "Processing…" : payMethod === "upi" ? `Submit UPI payment · ${inr(total)}` : `Pay ${inr(total)} · Place order`}
        </button>
      </div>
      <div className="md:hidden h-20" aria-hidden />
    </div>
  );
}

function Option({ active, onClick, testid, title, sub, right }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-full text-left p-4 border flex items-center gap-3 transition-colors ${active ? "border-[#D4AF37] bg-[#FFF9E6]" : "border-[#D4AF37]/30 bg-white"}`}>
      <span className={`w-4 h-4 rounded-full border-2 ${active ? "border-[#D4AF37] bg-[#D4AF37]" : "border-[#D4AF37]/50"}`} />
      <span className="flex-1"><span className="block text-sm text-[#0B1F3A] font-semibold">{title}</span><span className="block text-xs text-[#1E293B]/60">{sub}</span></span>
      {right && <span className="text-sm font-semibold text-[#D4AF37]">{right}</span>}
    </button>
  );
}

function DeliveryOptions({ value, onChange, pieces }) {
  return (
    <section className="bg-white p-6 gold-border" data-testid="delivery-options">
      <div className="font-serif text-lg text-[#0B1F3A] mb-4">Delivery option</div>
      <div className="space-y-3">
        <Option active={value === "standard"} onClick={() => onChange("standard")} testid="delivery-standard" title="Standard Delivery" sub="5–7 days" right="Free" />
        <Option active={value === "express"} onClick={() => onChange("express")} testid="delivery-express" title="Express Delivery" sub={`2 days · ₹${EXPRESS_FEE} per piece`} right={inr(EXPRESS_FEE * pieces)} />
      </div>
    </section>
  );
}

function PaymentOptions({ method, setMethod, utr, setUtr, total }) {
  const upiLink = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent("Nisa Libas")}&am=${total}&cu=INR`;
  return (
    <section className="bg-white p-6 gold-border" data-testid="payment-options">
      <div className="font-serif text-lg text-[#0B1F3A] mb-4">Payment</div>
      <div className="space-y-3">
        <Option active={method === "razorpay"} onClick={() => setMethod("razorpay")} testid="pay-razorpay" title="Pay online" sub="Cards · Netbanking · UPI via Razorpay" />
        <Option active={method === "upi"} onClick={() => setMethod("upi")} testid="pay-upi" title="Pay by UPI (PhonePe / GPay / Paytm)" sub="Scan QR or pay to our UPI ID, then enter the transaction ID" />
      </div>
      {method === "upi" && (
        <div className="mt-4 p-4 bg-[#FFF9E6] border border-[#D4AF37]/40 flex flex-col sm:flex-row gap-5 items-center" data-testid="upi-panel">
          <div className="bg-white p-3"><QRCodeSVG value={upiLink} size={160} data-testid="upi-qr" /></div>
          <div className="flex-1 w-full">
            <div className="text-xs text-[#1E293B]/60">UPI ID</div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-[#0B1F3A]" data-testid="upi-id">{UPI_ID}</span>
              <button type="button" onClick={() => { navigator.clipboard?.writeText(UPI_ID); toast.success("UPI ID copied"); }} className="text-xs underline text-[#0B1F3A]" data-testid="copy-upi">Copy</button>
            </div>
            <div className="text-xs text-[#1E293B]/70 mb-3">Pay <span className="font-semibold" data-testid="upi-amount">{inr(total)}</span> using PhonePe or any UPI app.</div>
            <a href={upiLink} className="sm:hidden inline-block mb-3 text-xs bg-[#0B1F3A] text-[#FFF9E6] px-3 py-2 tracking-widest uppercase" data-testid="upi-open-app">Open UPI app</a>
            <Input label="UPI transaction ID (UTR)" value={utr} onChange={setUtr} testid="upi-utr" />
          </div>
        </div>
      )}
    </section>
  );
}

function Input({ label, value, onChange, testid, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-xs text-[#1E293B]/60 mb-1">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full border px-3 py-2.5 text-sm bg-white" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid={testid} />
    </label>
  );
}
